import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../hooks/useAuth';
import { useCalendar } from '../hooks/useCalendar';
import { useInvestments } from '../hooks/useInvestments';
import { BuyInvestmentRequest, SellInvestmentRequest, UpdateInvestmentRequest } from '../services/api';
import InvestmentCalendar from '../components/Calendar';
import PortfolioSummaryComponent from '../components/PortfolioSummary';
import InvestmentForm from '../components/InvestmentForm';
import DailyPortfolioModal from '../components/DailyPortfolioModal';
import LoadingOverlay from '../components/LoadingOverlay';
import { getApiFailureMessage } from '../utils/apiErrorMessage';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { calendarDays, selectedDateRecords, fetchDateRecords, fetchCalendarData, isLoading: calendarLoading } = useCalendar();
  const { createBuyInvestment, createSellInvestment, updateInvestment, deleteInvestment, investments, fetchInvestments, isLoading: investmentsLoading } = useInvestments();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showForm, setShowForm] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState<{ year: number; month: number }>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });
  
  // 매수 기록에서 종목/통화 목록 추출 (매도 기록용)
  // 같은 종목은 하나로 그룹화하여 표시
  const getBuyStockList = React.useMemo(() => {
    const buyRecords = investments.filter(inv => inv.type === 'BUY' && !inv.isDeleted);
    const sellRecords = investments.filter(inv => inv.type === 'SELL' && !inv.isDeleted);
    
    // 종목별로 그룹화 (종목명 + 종목코드 조합을 키로 사용)
    const stockMap = new Map<string, { 
      name: string; 
      code?: string;
      currencyPair?: string;
      assetType?: string;
      baseCurrency?: string;
      quoteCurrency?: string;
      firstRecordId: number; // 첫 번째 매수 기록 ID (매도 시 사용)
      buyQuantity: number;
      sellQuantity: number;
    }>();
    
    // 매수 기록으로 종목/통화 정보 및 매수 수량 초기화
    buyRecords.forEach(record => {
      if (record.quantity && record.quantity > 0) {
        // 종목별 키 생성 (종목명 + 종목코드 또는 통화페어)
        const key = record.assetType === 'CURRENCY' 
          ? `${record.currencyPair || record.stockName}`
          : `${record.stockName}_${record.stockCode || ''}`;
        
        if (!stockMap.has(key)) {
          // 첫 번째 매수 기록을 대표로 사용
          stockMap.set(key, {
            name: record.stockName,
            code: record.stockCode,
            currencyPair: record.currencyPair,
            assetType: record.assetType,
            baseCurrency: record.baseCurrency,
            quoteCurrency: record.quoteCurrency,
            firstRecordId: record.id,
            buyQuantity: 0,
            sellQuantity: 0
          });
        }
        
        const stock = stockMap.get(key)!;
        stock.buyQuantity += record.quantity;
      }
    });
    
    // 매도 기록으로 매도 수량 누적
    sellRecords.forEach(record => {
      if (record.selectedStockId && record.sellQuantity) {
        // 매도 기록의 selectedStockId로 해당 매수 기록 찾기
        const buyRecord = buyRecords.find(r => r.id === record.selectedStockId);
        if (buyRecord) {
          const key = buyRecord.assetType === 'CURRENCY' 
            ? `${buyRecord.currencyPair || buyRecord.stockName}`
            : `${buyRecord.stockName}_${buyRecord.stockCode || ''}`;
          
          const stock = stockMap.get(key);
          if (stock) {
            stock.sellQuantity += record.sellQuantity;
          }
        }
      }
    });
    
    // 보유 수량이 있는 종목/통화만 필터링 (매수 수량 > 매도 수량)
    return Array.from(stockMap.values())
      .filter(stock => stock.buyQuantity > stock.sellQuantity)
      .map(stock => ({
        id: stock.firstRecordId, // 첫 번째 매수 기록 ID 사용
        label: stock.assetType === 'CURRENCY' 
          ? `${stock.currencyPair || stock.name} (외환)`
          : (stock.code ? `${stock.name} (${stock.code})` : stock.name),
        name: stock.name,
        code: stock.code,
        currencyPair: stock.currencyPair
      }));
  }, [investments]);

  // 데이터 로드 (로그인 여부와 관계없이)
  useEffect(() => {
    console.log('HomePage: 데이터 로드 시작, 로그인 상태:', isAuthenticated);
    // 캘린더: 이전·현재·다음 달 3개월치 API 호출 (Calendar는 calendarData 변경 시 날짜별 병합)
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear;
    const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
    const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear;

    void Promise.all([
      fetchCalendarData(prevYear, prevMonth),
      fetchCalendarData(currentYear, currentMonth),
      fetchCalendarData(nextYear, nextMonth),
    ]);

    // 포트폴리오/투자 기록은 PortfolioProvider / InvestmentsProvider가 초기 로드한다.
  }, [isAuthenticated, fetchCalendarData, fetchInvestments]);

  // 날짜 선택 처리
  const handleDateSelect = async (date: Date) => {
    setSelectedDate(date);
    // DailyPortfolioModal 표시
    setShowPortfolioModal(true);
  };

  // 투자 기록 제출 처리 (신규 생성) - Promise<boolean> 반환
  const handleInvestmentSubmit = async (record: any): Promise<boolean> => {
    try {
      if (record.type === 'buy') {
        const buyRequest: BuyInvestmentRequest = {
          recordDate: record.date,
          assetType: record.assetType || 'STOCK',
          stockName: record.stockName,
          stockCode: record.stockCode || undefined,
          currencyPair: record.currencyPair || undefined,
          baseCurrency: record.baseCurrency || undefined,
          quoteCurrency: record.quoteCurrency || undefined,
          exchangeRate: record.exchangeRate ? parseFloat(record.exchangeRate.toString()) : undefined,
          investmentRatio: record.investmentRatio,
          quantity: record.quantity ? parseInt(record.quantity.toString()) : undefined,
          pricePerShare: record.buyPrice || undefined,
          dividendPerShare: record.dividend || undefined,
          buyReason: record.buyReason || undefined
        };

        console.log('투자 기록 제출 시작:', buyRequest);
        
        const success = await createBuyInvestment(buyRequest);
        console.log('투자 기록 생성 결과:', success);
        
        if (success) {
          await fetchInvestments();
          await refreshCalendarAndRecords(record.date);
          return true;
        } else {
          alert('매수 기록 생성에 실패했습니다.');
          return false;
        }
      } else if (record.type === 'sell') {
        if (!record.selectedStockId) {
          alert('매도할 종목을 선택해주세요.');
          return false;
        }

        const sellRequest: SellInvestmentRequest = {
          recordDate: record.date,
          selectedStockId: parseInt(record.selectedStockId.toString()),
          sellQuantity: record.sellQuantity ? parseInt(record.sellQuantity.toString()) : undefined,
          sellRatio: record.sellRatio ? parseFloat(record.sellRatio.toString()) : undefined,
          sellPrice: (record.sellPrice !== undefined && record.sellPrice !== null && record.sellPrice !== '') 
            ? parseFloat(record.sellPrice.toString()) 
            : undefined,
          sellReason: record.sellReason || undefined
        };

        console.log('매도 기록 제출 시작:', sellRequest);
        
        const success = await createSellInvestment(sellRequest);
        console.log('매도 기록 생성 결과:', success);
        
        if (success) {
          await fetchInvestments();
          await refreshCalendarAndRecords(record.date);
          return true;
        } else {
          alert('매도 기록 생성에 실패했습니다.');
          return false;
        }
      }
      return false;
    } catch (error: any) {
      console.error('투자 기록 제출 오류:', error);
      alert(`오류가 발생했습니다: ${getApiFailureMessage(error)}`);
      return false;
    }
  };

  // 투자 기록 수정 처리 - Promise<boolean> 반환
  const handleInvestmentUpdate = async (recordId: number, record: any): Promise<boolean> => {
    try {
      if (record.type === 'buy') {
        const updateRequest: UpdateInvestmentRequest = {
          id: recordId,
          assetType: record.assetType || 'STOCK',
          stockName: record.stockName,
          stockCode: record.stockCode || undefined,
          currencyPair: record.currencyPair || undefined,
          baseCurrency: record.baseCurrency || undefined,
          quoteCurrency: record.quoteCurrency || undefined,
          exchangeRate: record.exchangeRate ? parseFloat(record.exchangeRate.toString()) : undefined,
          investmentRatio: record.investmentRatio,
          quantity: record.quantity ? parseInt(record.quantity.toString()) : undefined,
          pricePerShare: record.buyPrice ? parseFloat(record.buyPrice.toString()) : undefined,
          totalAmount: record.totalAmount ? parseFloat(record.totalAmount.toString()) : undefined,
          buyReason: record.buyReason || undefined
        };

        console.log('투자 기록 수정 시작:', updateRequest);
        
        const success = await updateInvestment(updateRequest);
        console.log('투자 기록 수정 결과:', success);
        
        if (success) {
          await fetchInvestments();
          await refreshCalendarAndRecords(record.date);
          return true;
        } else {
          alert('매수 기록 수정에 실패했습니다.');
          return false;
        }
      } else if (record.type === 'sell') {
        const updateRequest: UpdateInvestmentRequest = {
          id: recordId,
          sellQuantity: record.sellQuantity ? parseInt(record.sellQuantity.toString()) : undefined,
          sellRatio: record.sellRatio ? parseFloat(record.sellRatio.toString()) : undefined,
          sellPrice: (record.sellPrice !== undefined && 
                      record.sellPrice !== null && 
                      record.sellPrice !== '') 
            ? parseFloat(record.sellPrice.toString()) 
            : undefined,
          realizedProfitRate: (record.realizedProfitRate !== undefined && 
                               record.realizedProfitRate !== null && 
                               record.realizedProfitRate !== '') 
            ? parseFloat(record.realizedProfitRate.toString()) 
            : undefined,
          sellReason: record.sellReason || undefined
        };

        console.log('매도 기록 수정 시작:', updateRequest);
        
        const success = await updateInvestment(updateRequest);
        console.log('매도 기록 수정 결과:', success);
        
        if (success) {
          await fetchInvestments();
          await refreshCalendarAndRecords(record.date);
          return true;
        } else {
          alert('매도 기록 수정에 실패했습니다.');
          return false;
        }
      }
      return false;
    } catch (error: any) {
      console.error('투자 기록 수정 오류:', error);
      alert(`오류가 발생했습니다: ${getApiFailureMessage(error)}`);
      return false;
    }
  };

  // 투자 기록 삭제 처리
  const handleInvestmentDelete = async (recordId: number) => {
    try {
      // 삭제할 기록의 날짜를 먼저 가져옴
      const recordToDelete = investments.find(inv => inv.id === recordId);
      if (!recordToDelete) {
        alert('삭제할 기록을 찾을 수 없습니다.');
        return;
      }

      // 매수 기록인 경우, 연결된 매도 기록이 있는지 확인
      if (recordToDelete.type === 'BUY') {
        const relatedSellRecords = investments.filter(
          inv => inv.type === 'SELL' && inv.selectedStockId === recordId && !inv.isDeleted
        );
        
        if (relatedSellRecords.length > 0) {
          const sellCount = relatedSellRecords.length;
          const sellDates = relatedSellRecords.map(r => r.recordDate).join(', ');
          alert(
            `이 매수 기록과 연결된 매도 기록이 ${sellCount}개 있습니다.\n` +
            `매도 기록 날짜: ${sellDates}\n\n` +
            `매수 기록을 삭제하려면 먼저 연결된 매도 기록을 모두 삭제해주세요.`
          );
          return;
        }
      }

      // 삭제 확인
      const recordType = recordToDelete.type === 'BUY' ? '매수' : '매도';
      if (!window.confirm(`이 ${recordType} 기록을 삭제하시겠습니까?`)) {
        return;
      }

      const success = await deleteInvestment(recordId);
      
      if (success) {
        // 투자 기록 목록 새로고침 (매도 기록용 종목 목록 업데이트)
        await fetchInvestments();
        // 캘린더 및 기록 새로고침
        const dateStr = recordToDelete.recordDate;
        await refreshCalendarAndRecords(dateStr);
        // 모달 닫기
        setShowForm(false);
        alert('투자 기록이 성공적으로 삭제되었습니다.');
      } else {
        alert('투자 기록 삭제에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('투자 기록 삭제 오류:', error);
      alert(`오류가 발생했습니다: ${getApiFailureMessage(error)}`);
    }
  };

  // 캘린더 및 기록 새로고침 헬퍼 함수
  const refreshCalendarAndRecords = async (dateStr: string) => {
    const [year, month] = dateStr.split('-').map(Number);
    
    // 기록이 생성/수정된 월로 캘린더 갱신
    await fetchCalendarData(year, month);
    
    // 현재 달력이 보고 있는 월도 갱신 (다른 월에 기록했을 경우를 대비)
    if (currentCalendarMonth.year !== year || currentCalendarMonth.month !== month) {
      console.log('다른 월에 기록했으므로 현재 월도 갱신');
      await fetchCalendarData(currentCalendarMonth.year, currentCalendarMonth.month);
    }
    
    // 해당 날짜의 기록 다시 불러오기 (모달에 표시하기 위해)
    await fetchDateRecords(dateStr);
  };

  return (
    <div className="home-content">
      <LoadingOverlay isVisible={calendarLoading || investmentsLoading} message="홈 데이터를 불러오는 중..." />
      <PortfolioSummaryComponent key={`portfolio-${isAuthenticated}`} />
      
      <InvestmentCalendar 
        onDateSelect={handleDateSelect}
        calendarData={calendarDays}
        onMonthChange={(year, month) => {
          setCurrentCalendarMonth({ year, month });
          // prev/next 달 데이터는 Calendar.tsx onActiveStartDateChange에서 로드한다.
        }}
        fetchCalendarData={fetchCalendarData}
      />

      {showForm && (
        <InvestmentForm
          key={`form-${selectedDate.toISOString()}-${selectedDateRecords.length}`}
          onSubmit={handleInvestmentSubmit}
          onUpdate={handleInvestmentUpdate}
          onDelete={handleInvestmentDelete}
          onCancel={() => setShowForm(false)}
          selectedDate={selectedDate}
          existingRecords={selectedDateRecords}
          allInvestments={investments}
          existingStocks={getBuyStockList}
        />
      )}

      {showPortfolioModal && (
        <DailyPortfolioModal
          date={selectedDate}
          onClose={() => setShowPortfolioModal(false)}
        />
      )}
    </div>
  );
};

export default HomePage;

